import { useCallback, useState } from "react";
import { useQueryClient } from "react-query";
import { toast } from "react-toastify";

import { createFolder } from "../api/folders.api";
import type {
  FolderSummary,
  StorageErrorCode,
} from "../types/project-storage.types";
import { mapStorageError } from "../utils/storageErrors";
import { folderChildrenQueryKey } from "./useFolderChildren";
import { folderTreeQueryKey } from "./useFolderTree";

export type CreateFolderResult =
  | { ok: true; folder: FolderSummary }
  | { ok: false; errorCode?: StorageErrorCode; errorMessage: string };

export function useCreateFolder(
  mmCode: number | null,
  parentFolderId: string | null
) {
  const queryClient = useQueryClient();
  const [isCreating, setIsCreating] = useState(false);

  const createFolderItem = useCallback(
    async (displayName: string): Promise<CreateFolderResult> => {
      if (mmCode === null || !parentFolderId) {
        return {
          ok: false,
          errorMessage: "Selecione uma pasta para criar a subpasta.",
        };
      }

      setIsCreating(true);

      try {
        const folder = await createFolder(mmCode, {
          parentId: parentFolderId,
          displayName,
        });

        await Promise.all([
          queryClient.invalidateQueries(folderTreeQueryKey(mmCode)),
          queryClient.invalidateQueries(
            folderChildrenQueryKey(mmCode, parentFolderId)
          ),
        ]);

        toast.success("Pasta criada com sucesso.");
        return { ok: true, folder };
      } catch (error) {
        const mapped = mapStorageError(
          error,
          "action",
          "Não foi possível criar a pasta."
        );

        return {
          ok: false,
          errorCode: mapped.errorCode,
          errorMessage: mapped.message,
        };
      } finally {
        setIsCreating(false);
      }
    },
    [mmCode, parentFolderId, queryClient]
  );

  return {
    createFolderItem,
    isCreating,
  };
}
